/*
 * Upload checks shared by the browser and the services.
 * - CRC32C: the checksum Cloud Storage records for every object. The Studio computes it while a
 *   file uploads; processing compares it with what arrived, so a damaged upload is caught.
 * - Fingerprint: size plus the first and last few MB, hashed. Cheap to compute on a file of any
 *   size, and enough to say "this is probably already in the library" before uploading it again.
 */

// Slice-by-8 tables for the Castagnoli polynomial (reflected 0x82F63B78).
const TABLES = (() => {
  const t = Array.from({ length: 8 }, () => new Uint32Array(256));
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) c = c & 1 ? (c >>> 1) ^ 0x82f63b78 : c >>> 1;
    t[0]![i] = c >>> 0;
  }
  for (let i = 0; i < 256; i++) for (let k = 1; k < 8; k++) t[k]![i] = (t[0]![t[k - 1]![i]! & 0xff]! ^ (t[k - 1]![i]! >>> 8)) >>> 0;
  return t;
})();

/** CRC32C of `data`, continuing from `previous` (the CRC of everything before it; 0 to start). */
export function crc32c(data: Uint8Array, previous = 0): number {
  const [t0, t1, t2, t3, t4, t5, t6, t7] = TABLES as [Uint32Array, Uint32Array, Uint32Array, Uint32Array, Uint32Array, Uint32Array, Uint32Array, Uint32Array];
  let crc = ~previous >>> 0;
  let i = 0;
  const n = data.length;
  for (; i + 8 <= n; i += 8) {
    const a = (crc ^ (data[i]! | (data[i + 1]! << 8) | (data[i + 2]! << 16) | (data[i + 3]! << 24))) >>> 0;
    crc =
      t7[a & 0xff]! ^
      t6[(a >>> 8) & 0xff]! ^
      t5[(a >>> 16) & 0xff]! ^
      t4[a >>> 24]! ^
      t3[data[i + 4]!]! ^
      t2[data[i + 5]!]! ^
      t1[data[i + 6]!]! ^
      t0[data[i + 7]!]!;
  }
  for (; i < n; i++) crc = t0[(crc ^ data[i]!) & 0xff]! ^ (crc >>> 8);
  return ~crc >>> 0;
}

/** How Cloud Storage writes a CRC32C: base64 of the four bytes, big-endian. */
export function crc32cBase64(crc: number): string {
  const b = [(crc >>> 24) & 0xff, (crc >>> 16) & 0xff, (crc >>> 8) & 0xff, crc & 0xff];
  return btoa(String.fromCharCode(...b));
}

/** Bytes read from each end of a file for its fingerprint. */
export const FINGERPRINT_SAMPLE = 4 * 1024 * 1024;

/** The two byte ranges ([start, end), end exclusive) a fingerprint reads; small files are read whole. */
export function fingerprintRanges(size: number): [[number, number], [number, number]] {
  const head = Math.min(size, FINGERPRINT_SAMPLE);
  return [
    [0, head],
    [Math.max(head, size - FINGERPRINT_SAMPLE), size],
  ];
}

/** "v1:" + SHA-256 of the size and both samples. Same bytes, same fingerprint, in the browser or on a server. */
export async function fingerprint(size: number, head: Uint8Array, tail: Uint8Array): Promise<string> {
  const sizeBytes = new TextEncoder().encode(`${size}:`);
  const all = new Uint8Array(sizeBytes.length + head.length + tail.length);
  all.set(sizeBytes, 0);
  all.set(head, sizeBytes.length);
  all.set(tail, sizeBytes.length + head.length);
  const digest = new Uint8Array(await globalThis.crypto.subtle.digest("SHA-256", all));
  return `v1:${[...digest].map((b) => b.toString(16).padStart(2, "0")).join("")}`;
}
